'use client';

import { useState, useEffect } from 'react';

import { MyNotebooks } from './MyNotebooks/Mynotebooks';
import { NotebookDetail } from './MyNotebooks/MynotebooksDetail/MynotebooksDetail';
import { ChapterWise } from './ChapterWise/ChapterWise';
import { ChapterDetail } from './ChapterWise/ChapterDetail/ChapterDetail';
import { TestWise } from './TestWise/TestWise';
import { TestDetail } from './TestWise/TestwiseDetail/TestwiseDetail';
import NotebookSidebar from './shared/NotebookSidebar';
import MobileNotebookMenu from './shared/MobileNotebookMenu';
import { AppLayout } from '../Layout/AppLayout';
import { usePackStorage } from '../Layout/usePackStorage';
export default function NotebookFinal() {
  const [currentScreen, setCurrentScreen] = useState('my-notebooks');
  const [selectedNotebook, setSelectedNotebook] = useState(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(true);
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();

  const user = JSON.parse(localStorage.getItem('userProfile'));

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      // Treat as mobile/tablet only if width <= 1024
      setIsMobileOrTablet(width <= 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  useEffect(() => {
    window.webengage.track('Viewed Notebooks', {
      'User Id': user?.email,
    });
  }, []);

  const handleScreenChange = (screen) => {
    setCurrentScreen(screen);
    setSelectedNotebook(null);
    if (isMobileOrTablet) {
      setShowMobileMenu(false);
    }
  };

  const handleNotebookClick = (notebook, onNotebookUpdate, onNotebookDelete) => {
    setSelectedNotebook({
      ...notebook,
      onNotebookUpdate,
      onNotebookDelete,
    });
    const webengageData = {
      'User Id': user?.email,
      'Notebook Name': notebook.name,
      'Notebook Id': notebook._id,
    };
    if (notebook.type === 'chapter') {
      webengageData['Notebook Type'] = 'Chapter';
      setCurrentScreen('chapter-detail');
    } else if (notebook.type === 'test') {
      webengageData['Notebook Type'] = 'Test';
      setCurrentScreen('test-detail');
    } else {
      webengageData['Notebook Type'] = 'Custom';
      setCurrentScreen('notebook-detail');
    }

    window.webengage.track('Notebook Opened', webengageData);
  };

  const handleBack = () => {
    setSelectedNotebook(null);
    if (currentScreen === 'chapter-detail') {
      setCurrentScreen('chapter-wise');
    } else if (currentScreen === 'test-detail') {
      setCurrentScreen('test-wise');
    } else if (isMobileOrTablet && ['my-notebooks', 'chapter-wise', 'test-wise'].includes(currentScreen)) {
      setShowMobileMenu(true);
    } else {
      setCurrentScreen('my-notebooks');
    }
  };

  const renderCurrentScreen = () => {
    if (isMobileOrTablet && showMobileMenu) {
      return <MobileNotebookMenu onScreenChange={handleScreenChange} />;
    }

    switch (currentScreen) {
      case 'my-notebooks':
        return (
          <MyNotebooks
            onNotebookClick={handleNotebookClick}
            onScreenChange={handleScreenChange}
            onBack={isMobileOrTablet ? handleBack : null}
            selectedPack={selectedPack}
          />
        );
      case 'notebook-detail':
        return (
          <NotebookDetail
            notebook={selectedNotebook}
            onBack={handleBack}
            onNotebookUpdate={selectedNotebook?.onNotebookUpdate}
            onNotebookDelete={selectedNotebook?.onNotebookDelete}
          />
        );
      case 'chapter-wise':
        return (
          <ChapterWise
            onNotebookClick={handleNotebookClick}
            onScreenChange={handleScreenChange}
            onBack={isMobileOrTablet ? handleBack : null}
          />
        );
      case 'chapter-detail':
        return <ChapterDetail notebook={selectedNotebook} onBack={handleBack} />;
      case 'test-wise':
        return (
          <TestWise
            onNotebookClick={handleNotebookClick}
            onScreenChange={handleScreenChange}
            onBack={isMobileOrTablet ? handleBack : null}
          />
        );
      case 'test-detail':
        return <TestDetail notebook={selectedNotebook} onBack={handleBack} />;
      default:
        return (
          <MyNotebooks
            onNotebookClick={handleNotebookClick}
            onScreenChange={handleScreenChange}
            onBack={isMobileOrTablet ? handleBack : null}
            selectedPack={selectedPack}
          />
        );
    }
  };

  const shouldRenderNotebookSidebar =
    !isMobileOrTablet && ['my-notebooks', 'chapter-wise', 'test-wise'].includes(currentScreen);

  // ✅ Mobile view: Sidebar fixed at bottom
  if (isMobileOrTablet) {
    return (
      <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
        <div className="flex-1 min-w-0 pb-[80px]">
          {/* Adjust pb-[80px] as per Sidebar height */}
          {renderCurrentScreen()}
        </div>
      </AppLayout>
    );
  }

  // ✅ Desktop view remains unchanged
  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="flex-1 h-screen overflow-y-scroll">
        <div className="w-full flex flex-col items-center">
          <div className="w-full w-max-[840px] mt-8 flex flex-col md:flex-row gap-6 justify-center">
            {shouldRenderNotebookSidebar && (
              <div className="hidden md:block w-64 pl-0 md:ml-3 shrink-0">
                <div className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
                  <NotebookSidebar currentScreen={currentScreen} onScreenChange={handleScreenChange} />
                </div>
              </div>
            )}

            <div
              className={`flex-1 px-4 lg:px-0 md:pb-10 pb-20 ${
                ['notebook-detail', 'chapter-detail', 'test-detail'].includes(currentScreen) ? 'max-w-6xl' : 'max-w-5xl'
              }`}
            >
              {renderCurrentScreen()}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
